import { useState, useEffect, useRef } from 'react'
import { Search, Plus, FileText, Loader2, Trash2, MoreHorizontal } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Layout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { StatusBadge, TimeFilter, StatusFilter } from '@/components/scan'
import { ScanCreateModal } from '@/features/scan/ScanCreateModal'
import { DeleteScanDialog } from '@/features/scan/components/DeleteScanDialog'
import { useScans } from '@/lib/hooks/useScans'
import { formatDateTime, formatEndDateTime } from '@/lib/utils/timeFormat'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { scanService } from '@/lib/services/scanService'
import { useToast } from '@/lib/hooks/useToast'
import { useHasPermission } from '@/lib/hooks/usePermission'
import type { ScanFilters, TimeFramePreset, ScanStatus } from '@/lib/types'
import type { ScanListItem } from '@/lib/services/scanService'

function getTimeFrameDateRange(preset: TimeFramePreset): { from: Date; to: Date } | null {
  const now = new Date()

  switch (preset) {
    case 'last_15_minutes':
      return {
        from: new Date(now.getTime() - 15 * 60 * 1000),
        to: now,
      }
    case 'last_30_minutes':
      return {
        from: new Date(now.getTime() - 30 * 60 * 1000),
        to: now,
      }
    case 'last_45_minutes':
      return {
        from: new Date(now.getTime() - 45 * 60 * 1000),
        to: now,
      }
    case 'last_1_hour':
      return {
        from: new Date(now.getTime() - 60 * 60 * 1000),
        to: now,
      }
    case 'last_24_hours':
      return {
        from: new Date(now.getTime() - 24 * 60 * 60 * 1000),
        to: now,
      }
    default:
      return null
  }
}

export function ScanListPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [filters, setFilters] = useState<ScanFilters>({
    search: '',
    timeFrame: 'all',
  })
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [scanToDelete, setScanToDelete] = useState<ScanListItem | null>(null)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const previousScansRef = useRef<ScanListItem[]>([])

  useEffect(() => {
    document.title = `Media Scans - BGCH Crisis Buddy`
  }, [])

  const { activeCrisis } = useCrisisStore()
  // Include deleted scans when status filter is 'deleted', otherwise exclude them
  const includeDeleted = filters.status === 'deleted'
  const { scans, isLoading, error, refetch, updateUnreadyScans } = useScans(
    activeCrisis?.id,
    includeDeleted
  )
  const isClosedCrisis = activeCrisis?.status === 'closed'

  // Permission checks
  const canCreateScans = useHasPermission('scans.create')
  const canDeleteScans = useHasPermission('scans.delete')
  const canUpdateScans = useHasPermission('scans.update')

  // Poll for updates on scans that are queued or processing
  useEffect(() => {
    const hasActiveScans = scans.some(
      scan => scan.status === 'queued' || scan.status === 'processing'
    )

    if (hasActiveScans) {
      const intervalId = setInterval(() => {
        updateUnreadyScans()
      }, 1000) // Check every second

      return () => clearInterval(intervalId)
    }
    return undefined
  }, [scans, updateUnreadyScans])

  // Check for status changes and show toast for failures
  useEffect(() => {
    if (previousScansRef.current.length > 0) {
      scans.forEach(currentScan => {
        const previousScan = previousScansRef.current.find(s => s.id === currentScan.id)
        if (previousScan && previousScan.status !== 'failed' && currentScan.status === 'failed') {
          toast({
            title: 'Fout bij genereren van analyse',
            description:
              'Er is een fout opgetreden bij het verbinden met de AI-service. Probeer het opnieuw.',
            variant: 'destructive',
          })
        }
      })
    }
    previousScansRef.current = scans
  }, [scans, toast])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null)
    }

    if (openMenuId) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }

    return undefined
  }, [openMenuId])

  const handleDeleteClick = (scan: ScanListItem, e: React.MouseEvent) => {
    e.stopPropagation()
    setScanToDelete(scan)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!scanToDelete) return

    setIsDeleting(true)
    try {
      await scanService.deleteScan(scanToDelete.id)
      refetch()
      setDeleteDialogOpen(false)
      setScanToDelete(null)
    } catch (err) {
      console.error('Error deleting scan:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  const filteredScans = scans
    .filter(scan => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase()
        const matchesSearch =
          scan.title?.toLowerCase().includes(searchLower) ||
          scan.keywords.some(k => k.toLowerCase().includes(searchLower)) ||
          scan.location.toLowerCase().includes(searchLower)
        if (!matchesSearch) return false
      }

      // Status filter
      if (filters.status) {
        if (scan.status !== filters.status) {
          return false
        }
      }

      // Time frame filter
      if (filters.timeFrame !== 'custom' && filters.timeFrame !== 'all') {
        const timeRange = getTimeFrameDateRange(filters.timeFrame)
        if (timeRange) {
          const scanDate = new Date(scan.createdAt)
          if (scanDate < timeRange.from || scanDate > timeRange.to) {
            return false
          }
        }
      }

      return true
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  return (
    <Layout>
      {/* Hero Section */}
      <div className="container mx-auto px-4 md:px-6 mt-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground mb-2">
              {t('scan.titel')}
            </h1>
            <p className="text-base md:text-xl text-muted-foreground">{t('scan.beschrijving')}</p>
          </div>
          <Button
            variant="gradient"
            size="lg"
            className="gap-2 w-full md:w-auto"
            onClick={() => setIsCreateModalOpen(true)}
            disabled={isClosedCrisis || !canCreateScans}
            title={
              isClosedCrisis
                ? 'Kan geen nieuwe scans maken voor gesloten crisis'
                : !canCreateScans
                  ? 'Geen toestemming om scans te maken'
                  : ''
            }
          >
            <Plus className="w-5 h-5" />
            <span className="md:inline">{t('scan.nieuweAnalyse')}</span>
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 md:px-6 md:py-12">
        {/* Filters Bar */}
        <Card className="mb-6">
          <CardContent className="!pt-3 !pb-3 !px-3 md:!pt-3 md:!pb-3 md:!px-3 lg:!pt-3 lg:!pb-3 lg:!px-3">
            <fieldset>
              <legend className="sr-only">{t('scan.filters')}</legend>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Search */}
                <div className="relative">
                  <Label htmlFor="scan-search" className="sr-only">
                    {t('scan.zoeken')}
                  </Label>
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <Input
                    id="scan-search"
                    placeholder={t('scan.zoeken')}
                    value={filters.search}
                    onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="pl-10"
                    aria-label={t('scan.zoeken')}
                  />
                </div>

                {/* Time Filter */}
                <div>
                  <Label htmlFor="scan-timeframe" className="sr-only">
                    {t('scan.tijdperiode')}
                  </Label>
                  <TimeFilter
                    value={filters.timeFrame}
                    onChange={(value: TimeFramePreset) =>
                      setFilters(prev => ({ ...prev, timeFrame: value }))
                    }
                    className="w-full"
                  />
                </div>

                {/* Status Filter */}
                <div>
                  <Label htmlFor="scan-status" className="sr-only">
                    {t('scan.status.label')}
                  </Label>
                  <StatusFilter
                    value={filters.status}
                    onChange={value =>
                      setFilters(prev => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { status, ...rest } = prev
                        return value === undefined ? rest : { ...rest, status: value }
                      })
                    }
                    className="w-full"
                  />
                </div>
              </div>
            </fieldset>
          </CardContent>
        </Card>

        {/* Scan List - Full Width */}
        <div>
          {isLoading ? (
            <Card className="text-center py-12" role="status" aria-live="polite">
              <CardContent>
                <Loader2
                  className="w-16 h-16 text-muted-foreground mx-auto mb-4 animate-spin"
                  aria-hidden="true"
                />
                <h3 className="text-xl font-semibold mb-2">{t('scan.analysesLaden')}</h3>
                <p className="text-muted-foreground">{t('scan.analysesLadenBeschrijving')}</p>
              </CardContent>
            </Card>
          ) : error ? (
            /* Error State */
            <Card className="text-center py-12">
              <CardContent>
                <FileText className="w-16 h-16 text-red-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">{t('scan.foutBijLaden')}</h3>
                <p className="text-muted-foreground mb-6">
                  {t('scan.foutBijLadenBeschrijving', { error })}
                </p>
                <Button onClick={() => window.location.reload()} variant="outline">
                  {t('scan.opnieuwProberen')}
                </Button>
              </CardContent>
            </Card>
          ) : filteredScans.length === 0 ? (
            /* Empty State */
            <Card className="text-center py-12">
              <CardContent>
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">{t('scan.geenAnalysesGevonden')}</h3>
                <p className="text-muted-foreground mb-6">
                  {filters.search
                    ? t('scan.geenAnalysesGevondenZoeken')
                    : t('scan.geenAnalysesNogNiet')}
                </p>
                <Button
                  variant="gradient"
                  className="gap-2"
                  onClick={() => setIsCreateModalOpen(true)}
                  disabled={isClosedCrisis}
                  title={isClosedCrisis ? 'Kan geen nieuwe scans maken voor gesloten crisis' : ''}
                >
                  <Plus className="w-4 h-4" />
                  {t('scan.eersteAnalyseMaken')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            /* Scan List */
            <div className="space-y-4">
              {filteredScans.map(scan => {
                // Determine if the scan is accessible to the current user
                const isAccessible = scan.status === 'confirmed' || canUpdateScans
                const scanIsClickable = isAccessible && (scan.status === 'draft' || scan.status === 'confirmed')

                return (
                <Card
                  key={scan.id}
                  className={`hover:shadow-md transition-shadow ${scanIsClickable ? 'cursor-pointer' : ''} ${scan.status === 'deleted' ? 'opacity-60 border-muted' : ''} ${!isAccessible ? 'border-muted cursor-not-allowed' : ''}`}
                  title={!isAccessible ? 'U hebt geen toestemming om deze analyse te bekijken' : ''}
                >
                  <div
                    className={`block ${scanIsClickable ? 'cursor-pointer' : ''}`}
                    onClick={
                      scanIsClickable
                        ? () => (window.location.href = `/scan/detail/${scan.id}`)
                        : undefined
                    }
                  >
                    <CardContent className="p-4 md:p-6">
                      {/* First row: Date range left, Status and Actions menu right */}
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-0 mb-3 md:mb-4">
                        <div className="flex-1">
                          {scan.timeFrame?.from && scan.timeFrame?.to && (
                            <h3 className="text-sm md:text-lg font-semibold text-foreground">
                              {formatDateTime(scan.timeFrame.from)} -{' '}
                              {formatEndDateTime(scan.timeFrame.from, scan.timeFrame.to)}
                            </h3>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={scan.status as ScanStatus} />
                          {scan.status === 'processing' && (
                            <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                          )}
                          {scan.status !== 'deleted' && canDeleteScans && (
                            <div className="relative">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={e => {
                                  e.stopPropagation()
                                  setOpenMenuId(openMenuId === scan.id ? null : scan.id)
                                }}
                                disabled={isClosedCrisis}
                                title={
                                  isClosedCrisis
                                    ? 'Kan geen acties uitvoeren in gesloten crisis'
                                    : 'Acties openen'
                                }
                              >
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Acties openen</span>
                              </Button>
                              {openMenuId === scan.id && (
                                <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-border rounded-md shadow-lg p-1 z-50">
                                  {canDeleteScans && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="w-full justify-start gap-2 text-red-600 hover:text-red-600 hover:bg-red-50"
                                      onClick={e => {
                                        e.stopPropagation()
                                        handleDeleteClick(scan, e)
                                        setOpenMenuId(null)
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                      Verwijderen
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Second row: Keywords left, Created by right */}
                      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 md:gap-0">
                        <div className="flex-1">
                          {(scan.searchQuery || scan.keywords.length > 0) && (
                            <div>
                              <div className="flex flex-wrap gap-1 items-center">
                                <div className="text-xs md:text-sm font-medium text-muted-foreground me-2">
                                  Zoekterm:
                                </div>
                                <code className="text-xs md:text-sm font-mono text-foreground bg-muted/50 px-2 py-1 rounded border border-border">
                                  {scan.searchQuery || scan.keywords.length === 0 ? (
                                    scan.searchQuery || `(${scan.location})`
                                  ) : (
                                    <>
                                      ({scan.location}) AND ({scan.keywords.slice(0).join(' OR ')})
                                    </>
                                  )}
                                </code>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0 md:ml-4">
                          {scan.createdBy && (
                            <span className="text-xs md:text-sm text-muted-foreground">
                              Door:{' '}
                              {scan.createdBy.displayName ||
                                (scan.createdBy.firstName &&
                                scan.createdBy.firstName !== 'None' &&
                                scan.createdBy.firstName.trim() &&
                                scan.createdBy.lastName &&
                                scan.createdBy.lastName !== 'None' &&
                                scan.createdBy.lastName.trim()
                                  ? `${scan.createdBy.firstName} ${scan.createdBy.lastName}`
                                  : 'Unknown User')}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </div>
                </Card>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Create Scan Modal */}
      <ScanCreateModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onSuccess={refetch}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteScanDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        scan={scanToDelete}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </Layout>
  )
}
